import Card from "@rio-cloud/rio-uikit/Card";
import ContentLoader from "@rio-cloud/rio-uikit/ContentLoader";

interface FuelMixChartSkeletonProps {
  bars?: number;
  className?: string;
}

const TITLE_LINE_HEIGHT = 23;

const CHART_HEIGHT = 280;
const CHART_MARGIN_TOP = 30;
const CHART_MARGIN_BOTTOM = 15;
const CHART_MARGIN_X = 15;
const X_AXIS_HEIGHT = 30;
const PLOT_HEIGHT = CHART_HEIGHT - CHART_MARGIN_TOP - CHART_MARGIN_BOTTOM - X_AXIS_HEIGHT;
const PLOT_ROW_HEIGHT = CHART_MARGIN_TOP + PLOT_HEIGHT;

const BAR_SIZE = 50;
const BAR_RADIUS = 4;
const VALUE_LABEL_HEIGHT = 16;
const VALUE_LABEL_GAP = 5;
const X_TICK_HEIGHT = 17;
const X_TICK_WIDTH = "90%";

const BAR_PERCENTAGES = [58, 92, 44, 70, 36, 80];
const VALUE_LABEL_WIDTHS = ["92%", "100%", "84%", "96%", "80%", "100%"];

const DEFAULT_CARD_CLASS_NAME = "padding-y-25 padding-x-15 padding-x-25-sm";

export default function FuelMixChartSkeleton({
  bars = BAR_PERCENTAGES.length,
  className = DEFAULT_CARD_CLASS_NAME,
}: Readonly<FuelMixChartSkeletonProps>) {
  const columns = Array.from({ length: Math.max(0, bars) }, (_, index) => ({
    key: `fuel-mix-skeleton-bar-${index}`,
    height: Math.round((BAR_PERCENTAGES[index % BAR_PERCENTAGES.length] / 100) * PLOT_HEIGHT),
    labelWidth: VALUE_LABEL_WIDTHS[index % VALUE_LABEL_WIDTHS.length],
  }));

  return (
    <Card className={className}>
      <div className="padding-bottom-20">
        <span className="text-size-16 text-bold">
          <ContentLoader className="display-block" height={TITLE_LINE_HEIGHT} width={180} />
        </span>
      </div>

      <div className="width-100pct">
        <div className="display-flex flex-column" style={{ height: CHART_HEIGHT }}>
          <div
            className="display-flex align-items-end"
            style={{
              height: PLOT_ROW_HEIGHT,
              paddingLeft: CHART_MARGIN_X,
              paddingRight: CHART_MARGIN_X,
              justifyContent: "space-around",
            }}
          >
            {columns.map((column) => (
              <div
                key={column.key}
                className="display-flex flex-column align-items-center justify-content-end"
                style={{ flex: `0 1 ${BAR_SIZE}px`, minWidth: 0, gap: VALUE_LABEL_GAP }}
              >
                <ContentLoader height={VALUE_LABEL_HEIGHT} width={column.labelWidth} />
                <ContentLoader height={column.height} width="100%" rx={BAR_RADIUS} ry={BAR_RADIUS} />
              </div>
            ))}
          </div>

          <div
            className="display-flex align-items-center"
            style={{
              height: X_AXIS_HEIGHT,
              paddingLeft: CHART_MARGIN_X,
              paddingRight: CHART_MARGIN_X,
              justifyContent: "space-around",
            }}
          >
            {columns.map((column) => (
              <div
                key={column.key}
                className="display-flex justify-content-center"
                style={{ flex: `0 1 ${BAR_SIZE}px`, minWidth: 0 }}
              >
                <ContentLoader height={X_TICK_HEIGHT} width={X_TICK_WIDTH} />
              </div>
            ))}
          </div>

          <div style={{ height: CHART_MARGIN_BOTTOM }} />
        </div>
      </div>
    </Card>
  );
}
